import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { DataTable } from "@/components/molecules/tables/data-table";
import type { Customer } from "@/features/customers/types";
import { useMessages } from "@/features/messages/hooks/use-messages";
import { useTranslation } from "@/features/utils/i18n";
import { TrayIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useMessageColumns } from "../../messages/components/columns";

/**
 * What the AI left for the team about this customer — pending and handled alike,
 * what is still to do first, what was handled greyed out below it. The same columns and the same "Traiter" dialog as the Messagerie,
 * minus the customer column, which would name this contact on every row.
 */
export function CustomerMessagesTable({ customer }: { customer: Customer }) {
  const { t } = useTranslation("customers");
  const columns = useMessageColumns({ hideCustomer: true });

  // No status: the route then returns both pending and handled requests.
  const { data, isLoading, isError, refetch } = useMessages({
    customerId: customer.id,
  });

  // What is still waiting on the team first, then what was handled — newest
  // first within each.
  const messages = useMemo(
    () =>
      [...(data?.messages || [])].sort(
        (a, b) =>
          Number(a.status === "handled") - Number(b.status === "handled") ||
          (b.created_at || 0) - (a.created_at || 0),
      ),
    [data],
  );

  if (isError) return <ErrorState onRetry={refetch} />;

  return (
    <DataTable
      variant="embedded"
      columns={columns}
      data={messages}
      isLoading={isLoading}
      total={messages.length}
      isRowMuted={(message) => message.status === "handled"}
      emptyState={
        <EmptyState compact icon={TrayIcon} title={t("view.noMessages")} />
      }
    />
  );
}
